import {
  isTurkUserRole,
  isSmsDashboardOrUnregisteredRole,
  isRoleNameAdminTools,
  isRoleNameBillingTools,
  isAdmin,
  isAgencyDoctor,
  isPatient,
  hasAccessToCallDashboard,
  hasPatientProfileAccess,
  hasReportsAccess,
  hasCcmAgentDashboardAccess,
  hasInteractiveTimeDashboardAccess,
  hasPatientDetailsAccess,
  hasCcmDashboardAccess,
  hasCcmConsoleAccess,
  hasHomeDashboardAccess,
} from 'utils/userTypeHelper';
import { GroupedRoutes, Routes } from 'router/routes';
import { ModuleType, Roles, UserType } from 'infrastructure/enums';
import { RoleGroups } from 'infrastructure/consts/role-groups';

import {
  PLATFORM_MODE_LOCALSTORAGE_KEY,
  PLATFORM_MODE_PATIENT_ACCEPTANCE,
  PLATFORM_MODE_ADMIN_PANEL_ACCESS,
} from './constants';

export const hasClinicalRole = (roleNames: string[]) =>
  roleNames && roleNames.some((roleName) => roleName.includes(Roles.Clinical));

const hasRPMRole = (roleNames: string[]) =>
  roleNames &&
  roleNames.some(
    (roleName) =>
      !roleName.includes(Roles.AdminPanelAccess) &&
      !roleName.includes(Roles.CriticalAlerting) &&
      !roleName.includes(Roles.AtRiskAlerting),
  );

export const hasAccessToMainPage = (roleNames: Roles[]) =>
  roleNames.some((role) => RoleGroups.AccessToMainPageRoles.includes(role));

export const hasPatientAcceptanceRole = (roleNames: string[]) =>
  roleNames &&
  roleNames.some((roleName) => roleName.includes(Roles.PatientAcceptance));

export const isCriticalOrAtRiskAlertingRole = (roleNames: string[]) =>
  roleNames &&
  roleNames.every(
    (roleName) =>
      roleName === Roles.CriticalAlerting || roleName === Roles.AtRiskAlerting,
  );

export const isCriticalRole = (roleNames: string[]) =>
  roleNames &&
  roleNames.some((roleName) => roleName === Roles.CriticalAlerting);

export const isMedicalAssistantRole = (roleNames: string[]) =>
  roleNames &&
  roleNames.some((roleName) => roleName === Roles.MedicalAssistant);

export const isAtRiskRole = (roleNames: string[]) =>
  roleNames && roleNames.some((roleName) => roleName === Roles.AtRiskAlerting);

export const hasAdminPanelAccess = (roleNames: string[]) =>
  roleNames.includes(Roles.AdminPanelAccess);

export const isSupervisorRole = (roleNames: string[]) =>
  roleNames && roleNames.some((roleName) => roleName === Roles.Supervisor);

export const hasRoleNameCallManager = (roleNames: string[]) =>
  roleNames.includes(Roles.CallManager);

export const hasBillingToolsAccess = (roleNames: string[]) =>
  roleNames.includes(Roles.BillingTools);

export const getUserRoutes = (user: any) => {
  const { userType, roleNames } = user;

  const routes = new Set<string>([Routes.sso, Routes.overviewDashboard]);

  const addRoutes = (...newRoutes: string[]) => {
    newRoutes.forEach((route) => routes.add(route));
  };

  addRoutes(...GroupedRoutes.profile, ...GroupedRoutes.dashboard);

  if (isPatient(userType)) {
    addRoutes(Routes.patientGuid(user?.guid));
    return Array.from(routes);
  }

  if (isAdmin(userType)) {
    addRoutes(
      ...GroupedRoutes.adminPanelUsers,
      ...GroupedRoutes.adminPanelAgencyDoctor,
      ...GroupedRoutes.adminPanelClinics,
      ...GroupedRoutes.adminPanelAgencies,
      ...GroupedRoutes.adminPanelPatient,
      ...GroupedRoutes.adminPanelMedications,
      ...GroupedRoutes.adminPanelPapWorkplace,
      ...GroupedRoutes.patientAcceptance,
      ...GroupedRoutes.profile,
      ...GroupedRoutes.homeDashboard,
      ...GroupedRoutes.dashboard,
      // ...GroupedRoutes.ccmDashboard, // temp hide ccm console (CCM Home Dashboard)
      ...GroupedRoutes.patientDetails,
      ...GroupedRoutes.interactiveTimeDashboard,
      ...GroupedRoutes.ccmAgentDashboard,
      ...GroupedRoutes.adherenceSettings,
      ...GroupedRoutes.reports,
      ...GroupedRoutes.rpmAdd,
      ...GroupedRoutes.turkAdmin,
      ...GroupedRoutes.turkUser,
      ...GroupedRoutes.smsDashboard,
      ...GroupedRoutes.smsAutoReplySettings,
      ...GroupedRoutes.callDashboard,
      ...GroupedRoutes.patientProfile,
      ...GroupedRoutes.adminTools,
      ...GroupedRoutes.billingTools,
      ...GroupedRoutes.callManagement,
    );
  }

  if (isAgencyDoctor(userType)) {
    if (hasRPMRole(roleNames)) {
      addRoutes(...GroupedRoutes.rpmAdd);
    }
    if (isSupervisorRole(roleNames)) {
      addRoutes(...GroupedRoutes.patientsTools);
      addRoutes(...GroupedRoutes.adherenceSettings);
    }

    if (isTurkUserRole(roleNames)) {
      addRoutes(...GroupedRoutes.turkUser);
    }
  }

  if (hasAdminPanelAccess(roleNames)) {
    addRoutes(
      ...GroupedRoutes.adminPanelPatient,
      ...GroupedRoutes.adminPanelClinics,
      ...GroupedRoutes.adminPanelUsers,
      ...GroupedRoutes.adminPanelMedications,
      ...GroupedRoutes.adminPanelPapWorkplace,
    );

    if (isAgencyDoctor(userType))
      addRoutes(
        ...GroupedRoutes.adminPanelAgencies,
        ...GroupedRoutes.adminPanelAgencyDoctor,
      );
  }

  if (hasRoleNameCallManager(roleNames)) {
    addRoutes(...GroupedRoutes.callManagement);
  }

  const canAccessAdminTools = isAdmin(userType) || isAgencyDoctor(userType);

  if (canAccessAdminTools && isRoleNameAdminTools(roleNames)) {
    addRoutes(...GroupedRoutes.adminTools);
  }

  if (isRoleNameBillingTools(roleNames)) {
    addRoutes(...GroupedRoutes.billingTools);
  }

  if (hasAccessToCallDashboard(roleNames)) {
    addRoutes(...GroupedRoutes.callDashboard);
  }

  if (isSmsDashboardOrUnregisteredRole(roleNames)) {
    addRoutes(...GroupedRoutes.smsDashboard);
  }

  if (hasPatientAcceptanceRole(roleNames)) {
    addRoutes(...GroupedRoutes.patientAcceptance);
  }

  if (hasHomeDashboardAccess(userType)) {
    addRoutes(...GroupedRoutes.homeDashboard);
  }

  if (hasCcmDashboardAccess(userType, roleNames)) {
    addRoutes(...GroupedRoutes.ccmDashboard);
  }
  if (hasPatientDetailsAccess(userType, roleNames)) {
    addRoutes(...GroupedRoutes.patientDetails);
  }
  if (hasCcmAgentDashboardAccess(userType, roleNames)) {
    addRoutes(...GroupedRoutes.ccmAgentDashboard);
  }
  if (hasInteractiveTimeDashboardAccess(userType, roleNames)) {
    addRoutes(...GroupedRoutes.interactiveTimeDashboard);
  }
  if (hasPatientProfileAccess(userType, roleNames)) {
    addRoutes(...GroupedRoutes.patientProfile);
  }
  if (hasReportsAccess(userType, roleNames)) {
    addRoutes(...GroupedRoutes.reports);
  }
  if (hasCcmConsoleAccess(userType, roleNames)) {
    addRoutes(...GroupedRoutes.ccmConsole);
  }

  return Array.from(routes);
};

export const getUserPlatformMode = (user: any) => {
  const { userType, roleNames } = user;

  if (hasPatientAcceptanceRole(roleNames) && !hasAccessToMainPage(roleNames)) {
    return PLATFORM_MODE_PATIENT_ACCEPTANCE;
  }

  if (userType === UserType.HomeHealth) {
    if (hasAdminPanelAccess(roleNames) && !hasAccessToMainPage(roleNames)) {
      return PLATFORM_MODE_ADMIN_PANEL_ACCESS;
    }
  }

  if (
    userType === UserType.Practice &&
    hasAdminPanelAccess(roleNames) &&
    !hasAccessToMainPage(roleNames)
  ) {
    return PLATFORM_MODE_ADMIN_PANEL_ACCESS;
  }

  if (
    userType === UserType.Practice &&
    roleNames.length === 1 &&
    hasAdminPanelAccess(roleNames)
  )
    return PLATFORM_MODE_ADMIN_PANEL_ACCESS;

  const userPlatformMode = localStorage.getItem(PLATFORM_MODE_LOCALSTORAGE_KEY);
  if (userPlatformMode && userPlatformMode === ModuleType.RPM)
    return userPlatformMode;
  localStorage.setItem(PLATFORM_MODE_LOCALSTORAGE_KEY, ModuleType.RPM);
  return ModuleType.RPM;
};
